import { useEffect, useState, useCallback } from "react";
import { PublicNav, Footer } from "@/components/layout/PublicLayout";
import { ServiceCard } from "@/components/shared/ServiceCard";
import { useContent } from "@/context/ContentContext";
import api from "@/lib/api";
import { Search, SlidersHorizontal, MessageSquareText } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ServicesPublic() {
  const { t } = useContent();
  return (
    <div>
      <PublicNav />
      <div className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-5 py-14">
          <h1 className="font-display text-4xl font-bold tracking-tight">{t("services.page.title", "Catalogue de services")}</h1>
          <p className="mt-2 max-w-xl text-muted-foreground">{t("services.intro", "Comparez nos services par plateforme, catégorie et niveau de qualité. Prix transparents par 1 000.")}</p>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-5 py-10">
        <ServiceBrowser />
      </div>
      <Footer />
    </div>
  );
}

export function ServiceBrowser({ onOrder }) {
  const { t } = useContent();
  const [platforms, setPlatforms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ platform_id: "", category_id: "", quality: "", refill: "", search: "", sort: "recommended", custom_comments: "" });

  useEffect(() => { api.get("/platforms").then((r) => setPlatforms(r.data)); }, []);
  useEffect(() => {
    if (f.platform_id) api.get(`/categories?platform_id=${f.platform_id}`).then((r) => setCategories(r.data));
    else setCategories([]);
  }, [f.platform_id]);

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v !== "" && v !== "all") p.append(k, v); });
    p.append("limit", "24");
    const { data } = await api.get(`/services?${p.toString()}`);
    setItems(data.items); setTotal(data.total); setLoading(false);
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v === "all" ? "" : v, ...(k === "platform_id" ? { category_id: "" } : {}) }));

  return (
    <div>
      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 lg:flex-row lg:items-center">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input data-testid="service-search" value={f.search} onChange={(e) => set("search", e.target.value)} placeholder={t("services.search", "Rechercher un service...")} className="w-full bg-transparent py-2 text-sm outline-none" />
        </div>
        <div className="grid grid-cols-2 gap-2 lg:flex lg:items-center">
          <FilterSelect testid="filter-platform" value={f.platform_id || "all"} onChange={(v) => set("platform_id", v)} placeholder={t("services.filter.platform", "Plateforme")}
            options={[["all", t("services.filter.all", "Toutes")], ...platforms.map((p) => [p.id, p.name])]} />
          <FilterSelect testid="filter-category" value={f.category_id || "all"} onChange={(v) => set("category_id", v)} placeholder={t("services.filter.category", "Catégorie")}
            options={[["all", t("services.filter.all", "Toutes")], ...categories.map((c) => [c.id, c.name])]} />
          <FilterSelect testid="filter-sort" value={f.sort} onChange={(v) => set("sort", v)} placeholder={t("services.sort.label", "Trier")}
            options={[["recommended", t("services.sort.recommended", "Recommandé")], ["price_low", t("services.sort.price_low", "Prix croissant")], ["quality", t("services.sort.quality", "Qualité")], ["fastest", t("services.sort.fastest", "Plus rapide")]]} />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="tier-filter">
        <span className="mr-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("services.tier", "Palier")}</span>
        {[["", t("services.tier.all", "Tous")], ["economy", t("services.tier.economy", "Économie")], ["standard", t("services.tier.standard", "Standard")], ["premium", t("services.tier.premium", "Premium")]].map(([v, l]) => (
          <button key={v || "all"} data-testid={`tier-chip-${v || "all"}`} onClick={() => set("quality", v || "all")}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${(f.quality || "") === v ? "border-accent bg-accent text-white" : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
            {l}
          </button>
        ))}
        <span className="mx-1 hidden h-5 w-px bg-border sm:block" />
        <button data-testid="filter-custom-comments" onClick={() => set("custom_comments", f.custom_comments === "true" ? "all" : "true")}
          className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${f.custom_comments === "true" ? "border-accent bg-accent text-white" : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
          <MessageSquareText className="h-3.5 w-3.5" /> {t("services.filter.custom_comments", "Commentaires personnalisés")}
        </button>
      </div>

      <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"><SlidersHorizontal className="h-4 w-4" /> {total} {t("services.count", "service(s)")}</div>

      {loading ? (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-56 animate-pulse rounded-xl border border-border bg-secondary/40" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="mt-10 rounded-xl border border-dashed border-border p-16 text-center text-muted-foreground">{t("services.none", "Aucun service trouvé.")}</div>
      ) : (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((s) => <ServiceCard key={s.id} service={s} onOrder={onOrder} />)}
        </div>
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-full lg:w-40"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
